// @flow
import React, {useMemo, useState, useEffect} from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  makeStyles,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  TextField,
  Divider,
} from '@material-ui/core';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info,
  Add as AddIcon,
  Remove as RemoveIcon,
  ListAlt,
  List,
} from '@material-ui/icons';
import SortableList, {SortableItem, SortableKnob} from 'react-easy-sort';
import {colors} from '../../../../../assets/styles/colors';
import OptionItem from './OptionItem';
import TiendadaTooltip from '../../../../../elements/tiendada-tooltip';
import DocumentTextIcon from '../../../../../elements/icons/DocumentTextIcon';
import NumericIcon from '../../../../../elements/icons/NumericIcon';
import ViewGridIcon from '../../../../../elements/icons/ViewGridIcon';

import {useStoreOptionActions, useStoreSelector} from './OptionsContext';
import AddOptionsModal from './AddOptionsModal';

import type {ProductOptionGroup} from '../../../../../entities/product';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {listProductOptionGroupOptionsService} from '../../../../product-option/promises';
import {TiendadaSwitch} from '../../../../../elements/tiendada-switch';

const useStyles = makeStyles(theme => ({
  optionGroupItem: {
    border: `1px solid ${colors.grey300}`,
    borderRadius: '.75rem',
    backgroundColor: colors.grey100,
    position: 'relative',
  },
  dragHandle: {
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '42px',
    width: '28px',
    color: colors.grey400,
    padding: theme.spacing(0.5),
    position: 'absolute',
    left: '-1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,

    '&:active': {
      cursor: 'grabbing',
    },
  },
  optionCard: {
    padding: '.5rem .5rem .5rem .75rem',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
  },

  optionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  optionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  optionName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '.25rem',
    flexGrow: 1,

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  subtitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey700,
    whiteSpace: 'nowrap',
  },
  optionActions: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
  },
  optionsContainer: {
    borderTop: 'none',
    borderRadius: '0 0 .75rem .75rem',
    backgroundColor: 'inherit',
  },
  '@global': {
    '.dragged': {
      transform: 'rotate(8deg)',
      boxShadow: '0px 8px 24px rgba(66, 64, 64, 0.12)',
      backgroundColor: colors.lightPurple,
      border: `1px solid ${colors.primary} !important`,
      zIndex: 1000,
    },
  },
  optionInfo: {
    fontSize: '0.75rem',
    color: colors.grey600,
    marginBottom: '0.25rem',
    display: 'block',
  },
  rootIosSwitch: {
    margin: '0',
  },
  deleteButton: {
    color: colors.danger,
  },
  configurationSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  configRow: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  configGroup: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  configLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: colors.grey800,
  },
  radioGroup: {
    flexDirection: 'row',
    gap: '0.5rem',
  },
  radioLabel: {
    fontSize: '0.8rem',
    color: colors.grey600,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    '&.Mui-checked': {
      '& .MuiFormControlLabel-label': {
        color: colors.primary,
        fontWeight: '600',
      },
    },
  },
  radioIcon: {
    fontSize: '1.5rem',
    color: 'inherit',
  },
  numberInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  numberInput: {
    width: '100px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      '& fieldset': {
        borderColor: colors.grey300,
      },
      '&:hover fieldset': {
        borderColor: colors.grey400,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
      textAlign: 'center',
      fontSize: '0.875rem',
    },
  },
  incrementButton: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    borderRadius: '6px',
    border: `1px solid ${colors.grey300}`,
    backgroundColor: 'white',
    color: colors.grey600,
    '&:hover': {
      backgroundColor: colors.grey100,
      borderColor: colors.grey400,
    },
  },
  minMaxContainer: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  minMaxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    minWidth: '120px',
  },
  minMaxLabel: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: '500',
  },
  optionTagName: {
    fontSize: '0.75rem',
    color: colors.grey700,
    fontWeight: '500',
    backgroundColor: colors.grey200,
    borderRadius: '.5rem',
    padding: '.25rem .5rem',
    maxWidth: '30ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    [theme.breakpoints.up('md')]: {
      maxWidth: '50ch',
    },
  },
  optionGroupListName: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
    width: '80%',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
  },
  configurationSummary: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: '500',
  },
  expandedSectionButton: {
    cursor: 'pointer',

    '&:hover $subtitle': {
      color: colors.primary,
    },
  },
  addOptionButton: {
    width: '100%',
    margin: '.5rem 0 1rem',
    textTransform: 'none',
    borderRadius: '.75rem',
    color: colors.grey600,
    border: `1px dashed ${colors.grey600}`,
    backgroundColor: 'transparent',
  },
}));

type Props = {
  optionGroup: ProductOptionGroup,
};

const PRESENTATION_OPTIONS = [
  {
    value: 'LISTING',
    label: 'Lista',
    icon: List,
  },
  {
    value: 'GRID',
    label: 'Cuadrícula',
    icon: ViewGridIcon,
  },
];

const GROUP_OPTION_TYPE = [
  {
    value: 'MULTI_OPTION',
    label: 'Multi-opción',
    icon: ListAlt,
  },
  {
    value: 'TEXT',
    label: 'Texto',
    icon: DocumentTextIcon,
  },
  {
    value: 'NUMERIC',
    label: 'Numérico',
    icon: NumericIcon,
  },
];

function OptionGroupItem({optionGroup}: Props) {
  const classes = useStyles();

  const groupOptionId = optionGroup.storeProductOptionGroup.id;

  const optionsList = useStoreSelector(
    s => s.storeGroupOptions,
    storeGroupOptions => storeGroupOptions[groupOptionId]
  );

  const {
    orderGroupOptions,
    changeGroupConfiguration,
    removeOptionGroup,
    setOptionGroupEnabled,
    addNewStoreGroupOption,
    addNewOptions,
  } = useStoreOptionActions();

  const [isExpanded, setIsExpanded] = useState(optionGroup.isNew);
  const [settingExpanded, setSettingExpanded] = useState(false);
  const [addOptionsModalOpen, setAddOptionsModalOpen] = useState(false);

  const hasOptions = Array.isArray(optionsList) && optionsList.length > 0;
  useEffect(() => {
    if (hasOptions) return;
    listProductOptionGroupOptionsService(groupOptionId).then(response => {
      addNewStoreGroupOption(groupOptionId, response);
    });
  }, [addNewStoreGroupOption, groupOptionId, hasOptions]);

  const canAddNewOption =
    hasOptions && optionsList.length > optionGroup.options.length;

  const presentationType = optionGroup.config?.presentationType || 'LISTING';
  const fieldType = optionGroup.fieldType || 'MULTI_OPTION';
  const isRequired = optionGroup.required || false;
  const minValue = optionGroup.config?.minQuantity || 0;
  const maxValue = optionGroup.config?.maxQuantity || 1;

  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  const handleSettingExpand = () => {
    setSettingExpanded(prev => !prev);
  };

  const handleOptionSortEnd = (oldIndex: number, newIndex: number) => {
    orderGroupOptions(optionGroup.id, oldIndex, newIndex);
  };

  const handlePresentationTypeChange = event => {
    changeGroupConfiguration(optionGroup.id, {
      presentationType: event.target.value,
    });
  };

  const handleFieldTypeChange = event => {
    changeGroupConfiguration(optionGroup.id, {
      fieldType: event.target.value,
    });
  };

  const handleRequiredChange = event => {
    const newRequired = event.target.checked;

    changeGroupConfiguration(optionGroup.id, {
      required: newRequired,
    });
  };

  const handleMinValueChange = newValue => {
    changeGroupConfiguration(optionGroup.id, {
      minQuantity: newValue,
    });
  };

  const handleMaxValueChange = newValue => {
    changeGroupConfiguration(optionGroup.id, {
      maxQuantity: newValue,
    });
  };

  const incrementMinValue = () => {
    const newValue = minValue + 1;

    handleMinValueChange(newValue);
  };

  const decrementMinValue = () => {
    const newValue = Math.max(0, minValue - 1);
    handleMinValueChange(newValue);
  };

  const incrementMaxValue = () => {
    const newValue = maxValue + 1;
    handleMaxValueChange(newValue);
  };

  const decrementMaxValue = () => {
    const newValue = Math.max(1, maxValue - 1);

    handleMaxValueChange(newValue);
  };

  const handleDeleteGroup = () => {
    removeOptionGroup(optionGroup.id);
  };

  const handleToggleOption = (optionId, enabled) => {
    setOptionGroupEnabled(optionGroup.id, enabled);
  };

  const handleOpenAddOptionsModal = () => {
    setAddOptionsModalOpen(true);
  };

  const handleCloseAddOptionsModal = () => {
    setAddOptionsModalOpen(false);
  };

  const handleAddNewOptions = newOptions => {
    addNewOptions(optionGroup.id, newOptions);
  };

  const existingOptionIds = optionGroup.options.map(
    option => option.storeProductOption.id
  );

  const configurationSummary = useMemo(() => {
    const summary = [];

    if (isRequired) {
      summary.push('Requerido');
    }

    if (minValue > 0) {
      summary.push(`Mínimo ${minValue}`);
    }

    if (maxValue > 0) {
      summary.push(`Máximo ${maxValue}`);
    }

    if (fieldType === 'TEXT') {
      summary.push('Texto');
    }

    if (fieldType === 'NUMERIC') {
      summary.push('Numérico');
    }

    if (fieldType === 'MULTI_OPTION') {
      summary.push('Multi-opción');
    }

    if (presentationType === 'GRID') {
      summary.push('Cuadrícula');
    }

    if (presentationType === 'LISTING') {
      summary.push('Lista');
    }

    return summary.join(', ');
  }, [isRequired, minValue, maxValue, fieldType, presentationType]);

  return (
    <SortableItem key={optionGroup.id}>
      <div className={classes.optionGroupItem}>
        <SortableKnob>
          <span className={classes.dragHandle}>
            <DragIcon fontSize="small" />
          </span>
        </SortableKnob>
        <Box className={classes.optionCard}>
          <Box className={classes.optionContent}>
            <Box className={classes.optionHeader}>
              <Typography className={classes.optionName}>
                <TiendadaTooltip
                  placement="top"
                  title={`${optionGroup.options.length} opciones`}
                >
                  <span>{optionGroup.storeProductOptionGroup.name}</span>
                </TiendadaTooltip>

                <TiendadaTooltip
                  placement="top"
                  title="Puedes mantener los valores originales de cada opción o
              personalizarlos específicamente para este producto (precio y
              disponibilidad)."
                >
                  <Info color="primary" />
                </TiendadaTooltip>
              </Typography>
              <Box
                className={classes.optionActions}
                onClick={e => e.stopPropagation()}
              >
                <TiendadaSwitch
                  checked={optionGroup.enabled}
                  onChange={e =>
                    handleToggleOption(optionGroup.id, e.target.checked)
                  }
                  color="primary"
                  size="small"
                  classes={{root: classes.rootIosSwitch}}
                />
                <IconButton
                  size="small"
                  className={classes.deleteButton}
                  onClick={() => handleDeleteGroup()}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* ------ option setting section ------- */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            role="button"
            className={classes.expandedSectionButton}
            onClick={handleSettingExpand}
            pb={1}
          >
            <Typography className={classes.subtitle}>
              Configuración{' '}
              {!settingExpanded && (
                <span className={classes.configurationSummary}>
                  ({configurationSummary})
                </span>
              )}
            </Typography>
            <IconButton size="small">
              {settingExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={settingExpanded} timeout="auto" unmountOnExit>
            <Box className={classes.configurationSection}>
              <Box className={classes.configRow}>
                <Box className={classes.configGroup}>
                  <Typography className={classes.configLabel}>
                    Tipo de opción
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={fieldType}
                      onChange={handleFieldTypeChange}
                      className={classes.radioGroup}
                    >
                      {GROUP_OPTION_TYPE.map(option => {
                        const IconComponent = option.icon;
                        const isSelected = fieldType === option.value;

                        return (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio size="small" color="primary" />}
                            label={
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  color: isSelected
                                    ? colors.primary
                                    : 'inherit',
                                  fontWeight: isSelected ? '600' : 'normal',
                                }}
                              >
                                <IconComponent className={classes.radioIcon} />
                                <span>{option.label}</span>
                              </span>
                            }
                            className={classes.radioLabel}
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>

              <Box className={classes.configRow}>
                <Box className={classes.configGroup}>
                  <Typography
                    className={classes.configLabel}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    <span>Tipo de presentación</span>
                    {fieldType !== 'MULTI_OPTION' && (
                      <TiendadaTooltip
                        placement="top"
                        title="La Cuadrícula solo aplica para opciones de tipo multi-opción"
                      >
                        <Info color="primary" fontSize="small" />
                      </TiendadaTooltip>
                    )}
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={presentationType}
                      onChange={handlePresentationTypeChange}
                      className={classes.radioGroup}
                      disabled={fieldType !== 'MULTI_OPTION'}
                    >
                      {PRESENTATION_OPTIONS.filter(option => {
                        if (fieldType === 'MULTI_OPTION') {
                          return true;
                        }
                        return option.value === 'LISTING';
                      }).map(option => {
                        const IconComponent = option.icon;
                        const isSelected = presentationType === option.value;

                        return (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio size="small" color="primary" />}
                            label={
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  color: isSelected
                                    ? colors.primary
                                    : 'inherit',
                                  fontWeight: isSelected ? '600' : 'normal',
                                }}
                              >
                                <IconComponent className={classes.radioIcon} />
                                <span>{option.label}</span>
                              </span>
                            }
                            className={classes.radioLabel}
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>

              <Box className={classes.configRow}>
                <Box className={classes.configGroup}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRequired}
                        onChange={handleRequiredChange}
                        color="primary"
                      />
                    }
                    label={
                      <span className={classes.configLabel}>
                        Opción requerida
                      </span>
                    }
                    style={{fontSize: '0.875rem'}}
                  />
                </Box>
              </Box>

              {fieldType === 'MULTI_OPTION' && (
                <Box className={classes.configRow} pb={1}>
                  <Box className={classes.configGroup}>
                    <Typography className={classes.configLabel}>
                      Límites de selección
                    </Typography>
                    <Box className={classes.minMaxContainer}>
                      <Box className={classes.minMaxGroup}>
                        <Typography className={classes.minMaxLabel}>
                          Mínimo
                        </Typography>
                        <Box className={classes.numberInputContainer}>
                          <IconButton
                            size="small"
                            className={classes.incrementButton}
                            onClick={decrementMinValue}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            type="number"
                            value={minValue}
                            onChange={e => {
                              const value = parseInt(e.target.value) || 0;
                              handleMinValueChange(value);
                            }}
                            variant="outlined"
                            size="small"
                            className={classes.numberInput}
                            inputProps={{min: 0}}
                          />
                          <IconButton
                            size="small"
                            className={classes.incrementButton}
                            onClick={incrementMinValue}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box className={classes.minMaxGroup}>
                        <Typography className={classes.minMaxLabel}>
                          Máximo
                        </Typography>
                        <Box className={classes.numberInputContainer}>
                          <IconButton
                            size="small"
                            className={classes.incrementButton}
                            onClick={decrementMaxValue}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            type="number"
                            value={maxValue}
                            onChange={e => {
                              const value = parseInt(e.target.value) || 1;
                              handleMaxValueChange(value);
                            }}
                            variant="outlined"
                            size="small"
                            className={classes.numberInput}
                            inputProps={{min: 0}}
                          />
                          <IconButton
                            size="small"
                            className={classes.incrementButton}
                            onClick={incrementMaxValue}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>

          <Divider />

          {/* ------ group options section ------- */}

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            role="button"
            onClick={handleToggleExpand}
            sx={{gap: '0.5rem'}}
            className={classes.expandedSectionButton}
            py={1.5}
          >
            <Box className={classes.optionGroupListName}>
              <Typography className={classes.subtitle} whiteSpace="nowrap">
                Opciones ({optionGroup.options.length})
              </Typography>
              {!isExpanded && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  flexWrap="wrap"
                  sx={{gap: '0.5rem', overflowX: 'auto'}}
                >
                  {optionGroup.options.map(option => {
                    return (
                      <span key={option.id} className={classes.optionTagName}>
                        {option.storeProductOption.name}
                      </span>
                    );
                  })}
                </Box>
              )}
            </Box>
            <IconButton size="small">
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box className={classes.optionsContainer}>
              <SortableList
                onSortEnd={handleOptionSortEnd}
                draggedItemClassName="dragged"
                lockAxis="y"
              >
                {optionGroup.options.map((option, index) => {
                  return (
                    <OptionItem
                      key={option.id}
                      option={option}
                      groupId={optionGroup.id}
                    />
                  );
                })}
              </SortableList>

              {canAddNewOption && (
                <CustomButton
                  fullWidth
                  className={classes.addOptionButton}
                  color="default"
                  onClick={handleOpenAddOptionsModal}
                >
                  <AddIcon />
                  Agregar opción
                </CustomButton>
              )}
            </Box>
          </Collapse>
        </Box>

        <AddOptionsModal
          open={addOptionsModalOpen}
          onClose={handleCloseAddOptionsModal}
          onAddOptions={handleAddNewOptions}
          availableOptions={optionsList || []}
          existingOptionIds={existingOptionIds}
          groupName={optionGroup.storeProductOptionGroup.name}
        />
      </div>
    </SortableItem>
  );
}

const MemoOptionGroupItem = React.memo(OptionGroupItem);

export default MemoOptionGroupItem;
